import { BackgroundVisibility } from "building-types/settings/sheetsTypesEnum";
import {
  sheetAddGeometry,
  sheetGeometryListNew,
  sheetPolylineGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { percentNew, sheetPointsNew } from "common/utils/nominalNumbers";
import type { Plane } from "geom/planeTypes";
import { vec3Normalize } from "geom/vec3";
import type { Linework3DRenderingConfig } from "./Linework3DRenderingConfig";
import { defaultLinework3DConfig } from "./Linework3DRenderingConfig";
import type { PerspectiveDrawing } from "./perspective";
import { renderPerspectiveLines, renderPerspectiveShadedPolygons } from "./perspective";

export interface MakeLinework3DGeometryListOptions extends PerspectiveDrawing {
  readonly sectionClippingPlane?: Plane;
  readonly renderingConfig?: Linework3DRenderingConfig<any>;
  readonly backgroundVisibility?: BackgroundVisibility;
}

export function makeLinework3DGeometryList({
  groundZ,
  sceneBSP,
  occlusionBSP = null,
  roofVolumesBSP = null,
  viewMatrix,
  isPerspectiveMatrix,
  projectionMatrix,
  clippingPlanes,
  sectionClippingPlane,
  isPlanView,
  isElevation,
  enableShading,
  backgroundVisibility,
  renderingConfig = defaultLinework3DConfig,
}: MakeLinework3DGeometryListOptions) {
  const planGeometryList = sheetGeometryListNew();

  const fullBackgroundVisibility = !backgroundVisibility ? true : backgroundVisibility === BackgroundVisibility.FULL;
  const lineStyleOverride = fullBackgroundVisibility
    ? undefined
    : {
        strokeColor: colors.stoneLightX1,
      };

  const renderingSources = renderPerspectiveLines({
    sceneBSP,
    occlusionBSP: occlusionBSP ?? undefined,
    roofVolumesBSP: roofVolumesBSP ?? undefined,
    viewMatrix,
    isPerspectiveMatrix,
    isElevation,
    prjMatrix: projectionMatrix,
    clippingPlanes,
    isPlanView,
    groundZ,
    sectionClippingPlane,
  });

  if (enableShading) {
    const [lightDirX, lightDirY, lightDirZ] = vec3Normalize(-0.25, 1, -1);

    const shadedPolygons = renderPerspectiveShadedPolygons({
      sceneBSP,
      viewMatrix,
      isPerspectiveMatrix,
      prjMatrix: projectionMatrix,
      lightDirX,
      lightDirY,
      lightDirZ,
      clippingPlanes,
      isPlanView,
    });

    for (const i of Object.keys(shadedPolygons)) {
      const polygon = shadedPolygons[i];
      const intensity = Math.min(Math.max(polygon.shadingFactor, 0), 1);
      const intensityString = Math.floor(0xff * intensity).toString(16);
      const color = "#" + intensityString + intensityString + intensityString;

      sheetAddGeometry(
        planGeometryList,
        sheetPolylineGeometryNew({
          paths: [polygon.loop],
          linewidth: sheetPointsNew(0.002),
          fill: true,
          strokeColor: color,
          fillColor: color,
          fillOpacity: percentNew(1),
          strokeOpacity: percentNew(1),
          dash: false,
        }),
      );
    }
  }

  const { styles, layers } = renderingConfig;

  for (const layer of layers) {
    const { source, style } = layer;

    const paths = renderingSources[source];

    const renderStyle = style ? styles[style] : undefined;

    sheetAddGeometry(
      planGeometryList,
      sheetPolylineGeometryNew({
        paths,
        renderFast: true,
        ...renderStyle,
        ...lineStyleOverride,
      }),
    );
  }

  return planGeometryList;
}
